import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataContext } from '../../context/DataContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const AddGirvi = () => {
  const navigate = useNavigate();
  const { addGirvi, loading } = useContext(DataContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    customerName: '',
    mobileNo: '',
    itemName: '',
    itemWeight: '',
    principal: '',
    interestRate: '',
    startDate: new Date()
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatToMMDDYYYY = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const girviData = {
        customerName: formData.customerName,
        mobileNo: formData.mobileNo,
        itemName: formData.itemName,
        itemWeight: parseFloat(formData.itemWeight) || 0,
        principal: parseFloat(formData.principal),
        interestRate: parseFloat(formData.interestRate),
        startDate: formatToMMDDYYYY(formData.startDate),
        interestTillDate: formatToMMDDYYYY(formData.startDate),
        interestAmount: 0,
        initialPrincipal: parseFloat(formData.principal),
        totalPayable: parseFloat(formData.principal)
      };

      const result = await addGirvi(girviData);
      
      if (result.success) {
        setSuccess('Girvi Added Successfully');
        setFormData({
          customerName: '',
          mobileNo: '',
          itemName: '',
          itemWeight: '',
          principal: '',
          interestRate: '',
          startDate: new Date()
        });
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(result.error || 'Failed to add girvi');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        minHeight: '100vh',
        py: 4,
        px: { xs: 2, sm: 3, md: 4 }
      }}>
        <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
          {/* Back Button */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              borderRadius: 2,
              color: 'text.primary',
              borderColor: isDark 
                ? 'rgba(148, 163, 184, 0.1)' 
                : 'rgba(255, 255, 255, 0.2)',
              backgroundColor: isDark 
                ? 'rgba(30, 41, 59, 0.3)' 
                : 'rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: isDark 
                  ? 'rgba(30, 41, 59, 0.5)' 
                  : 'rgba(255, 255, 255, 0.5)',
              }
            }}
            variant="outlined"
          >
            Back
          </Button>

          {/* Main Card */}
          <Paper 
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              backgroundColor: isDark 
                ? 'rgba(30, 41, 59, 0.4)'
                : 'rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: `1px solid ${isDark 
                ? 'rgba(148, 163, 184, 0.1)'
                : 'rgba(255, 255, 255, 0.2)'
              }`,
              borderRadius: '24px',
              boxShadow: isDark 
                ? '0 8px 32px rgba(0, 0, 0, 0.2)'
                : '0 8px 32px rgba(31, 38, 135, 0.1)',
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 800,
                  mb: 1.5,
                  background: isDark
                    ? 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)'
                    : 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' }
                }}
              >
                Add New Girvi
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  maxWidth: '600px',
                  mx: 'auto'
                }}
              >
                Enter customer and loan details to create a new girvi record
              </Typography>
            </Box>

            {/* Alerts */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: '12px',
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  color: theme.palette.error.main,
                  '& .MuiAlert-icon': {
                    color: theme.palette.error.main
                  }
                }}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3,
                  borderRadius: '12px',
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  color: theme.palette.success.main,
                  '& .MuiAlert-icon': {
                    color: theme.palette.success.main
                  }
                }}
              >
                {success}
              </Alert>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Customer Name */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Customer Name"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: isDark 
                          ? 'rgba(30, 41, 59, 0.5)' 
                          : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        borderColor: isDark 
                          ? 'rgba(51, 65, 85, 0.6)' 
                          : 'rgba(226, 232, 240, 0.6)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: isDark 
                            ? 'rgba(30, 41, 59, 0.7)' 
                            : 'rgba(255, 255, 255, 0.9)',
                          borderColor: isDark 
                            ? 'rgba(51, 65, 85, 0.8)' 
                            : 'rgba(226, 232, 240, 0.8)',
                        },
                        '&.Mui-focused': {
                          borderColor: isDark ? '#3b82f6' : '#10b981',
                          backgroundColor: isDark 
                            ? 'rgba(30, 41, 59, 0.8)' 
                            : 'rgba(255, 255, 255, 0.95)',
                          boxShadow: `0 0 0 3px ${alpha(isDark ? '#3b82f6' : '#10b981', 0.1)}`
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary',
                        fontWeight: 500
                      }
                    }}
                  />
                </Grid>

                {/* Mobile Number */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Mobile No"
                    name="mobileNo"
                    value={formData.mobileNo}
                    onChange={handleChange}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: isDark 
                          ? 'rgba(30, 41, 59, 0.5)' 
                          : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        borderColor: isDark 
                          ? 'rgba(51, 65, 85, 0.6)' 
                          : 'rgba(226, 232, 240, 0.6)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: isDark 
                            ? 'rgba(30, 41, 59, 0.7)' 
                            : 'rgba(255, 255, 255, 0.9)',
                          borderColor: isDark 
                            ? 'rgba(51, 65, 85, 0.8)' 
                            : 'rgba(226, 232, 240, 0.8)',
                        },
                        '&.Mui-focused': {
                          borderColor: isDark ? '#3b82f6' : '#10b981',
                          backgroundColor: isDark 
                            ? 'rgba(30, 41, 59, 0.8)' 
                            : 'rgba(255, 255, 255, 0.95)',
                          boxShadow: `0 0 0 3px ${alpha(isDark ? '#3b82f6' : '#10b981', 0.1)}`
                        }
                      }
                    }}
                  />
                </Grid>

                {/* Item Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Item Name"
                    name="itemName"
                    value={formData.itemName}
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: isDark 
                          ? 'rgba(30, 41, 59, 0.5)' 
                          : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        borderColor: isDark 
                          ? 'rgba(51, 65, 85, 0.6)' 
                          : 'rgba(226, 232, 240, 0.6)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  />
                </Grid>

                {/* Item Weight */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Item Weight (g)"
                    name="itemWeight"
                    type="text"
                    value={formData.itemWeight}
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: isDark 
                          ? 'rgba(30, 41, 59, 0.5)' 
                          : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        borderColor: isDark 
                          ? 'rgba(51, 65, 85, 0.6)' 
                          : 'rgba(226, 232, 240, 0.6)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  />
                </Grid>

                {/* Principal Amount */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Principal Amount"
                    name="principal"
                    type="text"
                    value={formData.principal}
                    onChange={handleChange}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: isDark 
                          ? 'rgba(30, 41, 59, 0.5)' 
                          : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        borderColor: isDark 
                          ? 'rgba(51, 65, 85, 0.6)' 
                          : 'rgba(226, 232, 240, 0.6)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  />
                </Grid>

                {/* Interest Rate */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Interest Rate (%)"
                    name="interestRate"
                    type="text"
                    value={formData.interestRate}
                    onChange={handleChange}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: isDark 
                          ? 'rgba(30, 41, 59, 0.5)' 
                          : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        borderColor: isDark 
                          ? 'rgba(51, 65, 85, 0.6)' 
                          : 'rgba(226, 232, 240, 0.6)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  />
                </Grid>

                {/* Start Date */}
                <Grid item xs={12}>
                  <DatePicker
                    label="Start Date"
                    value={formData.startDate}
                    onChange={(date) => setFormData(prev => ({ ...prev, startDate: date }))}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        fullWidth 
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: isDark 
                              ? 'rgba(30, 41, 59, 0.5)' 
                              : 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            borderColor: isDark 
                              ? 'rgba(51, 65, 85, 0.6)' 
                              : 'rgba(226, 232, 240, 0.6)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: isDark 
                                ? 'rgba(51, 65, 85, 0.8)' 
                                : 'rgba(226, 232, 240, 0.8)',
                            }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 1.8,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem',
                      background: isDark
                        ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      boxShadow: isDark
                        ? '0 4px 14px 0 rgba(59, 130, 246, 0.35)'
                        : '0 4px 14px 0 rgba(16, 185, 129, 0.35)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background: isDark
                          ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                          : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        boxShadow: isDark
                          ? '0 6px 20px 0 rgba(59, 130, 246, 0.45)'
                          : '0 6px 20px 0 rgba(16, 185, 129, 0.45)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': {
                        background: 'rgba(0, 0, 0, 0.12)',
                        color: 'rgba(0, 0, 0, 0.26)'
                      }
                    }}
                  >
                    {loading ? (
                      <CircularProgress 
                        size={24} 
                        sx={{ 
                          color: isDark ? '#ffffff' : '#ffffff' 
                        }} 
                      />
                    ) : (
                      'Save Girvi'
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AddGirvi;